"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Header } from "@/app/components/Header";
import { createManualListing } from "@/lib/queries";

// For deals found outside the Route Consultant / FXG scrapers (per
// user report -- listings that show up outside the two scraped
// sources). Created listings get source='Manual', are permanently
// excluded from the daily pipeline's sold-marking check (see
// ingest_to_supabase.py), and pick up a Default Offer automatically
// on the next seed_default_offers_to_supabase.py run, same as any
// scraped listing.
export default function AddListingPage() {
  const router = useRouter();

  const [title, setTitle] = useState("");
  const [listingUrl, setListingUrl] = useState("");
  const [routeType, setRouteType] = useState<"" | "P&D" | "Linehaul">("");
  const [location, setLocation] = useState("");
  const [askingPrice, setAskingPrice] = useState("");
  const [revenue, setRevenue] = useState("");
  const [cashFlowNoi, setCashFlowNoi] = useState("");
  const [routesCount, setRoutesCount] = useState("");
  const [fleetValue, setFleetValue] = useState("");
  const [totalEmployees, setTotalEmployees] = useState("");
  const [trucks, setTrucks] = useState("");
  const [description, setDescription] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = title.trim() !== "";

  const numOrUndefined = (v: string): number | undefined => (v.trim() === "" ? undefined : Number(v));

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setError(null);
    try {
      const listing = await createManualListing({
        title: title.trim(),
        listing_url: listingUrl.trim() || undefined,
        route_type: routeType || undefined,
        location: location.trim() || undefined,
        asking_price: numOrUndefined(askingPrice),
        revenue: numOrUndefined(revenue),
        cash_flow_noi: numOrUndefined(cashFlowNoi),
        routes_count: numOrUndefined(routesCount),
        fleet_value: numOrUndefined(fleetValue),
        total_employees: numOrUndefined(totalEmployees),
        trucks: numOrUndefined(trucks),
        description: description.trim() || undefined,
      });
      router.push(`/listings/${encodeURIComponent(listing.uid)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-8">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Add Listing</h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          For deals found outside the automatically-scraped sources. A Default Offer will be created
          for it automatically on the next daily run.
        </p>

        {error && (
          <div
            className="mt-4 rounded-md border px-4 py-3 text-sm"
            style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
          >
            {error}
          </div>
        )}

        <div className="mt-6 space-y-4">
          <Field label="Title" required>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 12 FedEx Ground Routes - Denver (Aurora), CO"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
            />
          </Field>

          <Field label="Listing URL">
            <input
              type="text"
              value={listingUrl}
              onChange={(e) => setListingUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Route Type">
              <select
                value={routeType}
                onChange={(e) => setRouteType(e.target.value as "" | "P&D" | "Linehaul")}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-accent)]"
              >
                <option value="">—</option>
                <option value="P&D">P&D</option>
                <option value="Linehaul">Linehaul</option>
              </select>
            </Field>
            <Field label="Location">
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, ST"
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Asking Price">
              <NumberInput value={askingPrice} onChange={setAskingPrice} prefix="$" />
            </Field>
            <Field label="Revenue">
              <NumberInput value={revenue} onChange={setRevenue} prefix="$" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Cash Flow (NOI)">
              <NumberInput value={cashFlowNoi} onChange={setCashFlowNoi} prefix="$" />
            </Field>
            <Field label="Fleet Value">
              <NumberInput value={fleetValue} onChange={setFleetValue} prefix="$" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Routes">
              <NumberInput value={routesCount} onChange={setRoutesCount} />
            </Field>
            <Field label="Trucks">
              <NumberInput value={trucks} onChange={setTrucks} />
            </Field>
            <Field label="Total Employees">
              <NumberInput value={totalEmployees} onChange={setTotalEmployees} />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Operation highlights, notes…"
              className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-accent)]"
            />
          </Field>
        </div>

        <button
          onClick={handleSave}
          disabled={!isValid || saving}
          className="mt-6 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50"
          style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
        >
          {saving ? "Creating…" : "Create Listing"}
        </button>
      </main>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-[var(--color-text-secondary)]">
      {label}
      {required && <span style={{ color: "var(--color-negative)" }}> *</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  prefix,
}: {
  value: string;
  onChange: (v: string) => void;
  prefix?: string;
}) {
  return (
    <div className="relative">
      {prefix && (
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-[var(--color-text-tertiary)]">
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-accent)]"
        style={{ paddingLeft: prefix ? "20px" : "12px", paddingRight: "12px" }}
      />
    </div>
  );
}
